import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { isEmpty } from "lodash-es";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { useApi } from "../../shared/api/index";
import { useNotification } from "~hooks/useNotification";
import { INVALIDATE_PERMISSIONS_SCOPE_CACHE_KEY } from "~constants/permissions";

export const useFetchSeason = (params = {}) => {
  const { fetchData } = useApi();
  const { organizationId, sport, seasonId } = useParams();
  let URL = `${sport}/o/${organizationId}/seasons/${seasonId}`;
  if (!isEmpty(params)) {
    URL += `?${new URLSearchParams(params)?.toString()}`;
  }

  return useQuery({
    queryKey: ["seasons", sport, organizationId, seasonId, JSON.stringify(params)],
    queryFn: async () => {
      return await fetchData({
        url: URL,
      });
    },
  });
};

export const useCreateSeason = () => {
  const { sport, organizationId, competitionId } = useParams();
  const queryClient = useQueryClient();
  const { mutateData } = useApi();
  const { formatMessage } = useIntl();
  const notification = useNotification();

  return useMutation({
    mutationFn: async (data) => {
      return await mutateData({
        url: `${sport}/o/${organizationId}/competitions/${competitionId}/seasons`,
        method: "POST",
        data,
      });
    },
    onSuccess: async () => {
      notification.success({
        message: formatMessage({
          id: "season.add.success",
          defaultMessage: "Season added successfully",
        }),
        placement: "bottomRight",
      });

      localStorage.setItem(INVALIDATE_PERMISSIONS_SCOPE_CACHE_KEY, "true");

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["seasons"] }),
        queryClient.invalidateQueries({ queryKey: ["permission-scope-current-user"] }),
      ]);
    },
  });
};

export const useUpdateSeason = () => {
  const { sport, organizationId, seasonId } = useParams();
  const queryClient = useQueryClient();
  const { mutateData } = useApi();
  const { formatMessage } = useIntl();
  const notification = useNotification();

  return useMutation({
    mutationFn: async (data) => {
      return await mutateData({
        url: `${sport}/o/${organizationId}/seasons/${seasonId}`,
        method: "PUT",
        data,
      });
    },
    onSuccess: async () => {
      notification.success({
        message: formatMessage({
          id: "season.update.success",
          defaultMessage: "Season updated successfully",
        }),
        placement: "bottomRight",
      });

      localStorage.setItem(INVALIDATE_PERMISSIONS_SCOPE_CACHE_KEY, "true");

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["seasons"] }),
        queryClient.invalidateQueries({ queryKey: ["permission-scope-current-user"] }),
      ]);
    },
  });
};

export const useDeleteSeason = () => {
  const { sport, organizationId } = useParams();
  const queryClient = useQueryClient();
  const { mutateData } = useApi();
  const { formatMessage } = useIntl();
  const notification = useNotification();

  return useMutation({
    mutationFn: async (seasonId) => {
      return await mutateData({
        url: `${sport}/o/${organizationId}/seasons/${seasonId}`,
        method: "DELETE",
      });
    },
    onSuccess: async () => {
      notification.success({
        message: formatMessage({
          id: "season.delete.success",
          defaultMessage: "Season deleted successfully",
        }),
        placement: "bottomRight",
      });

      localStorage.setItem(INVALIDATE_PERMISSIONS_SCOPE_CACHE_KEY, "true");

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["seasons"] }),
        queryClient.invalidateQueries({ queryKey: ["permission-scope-current-user"] }),
      ]);
    },
  });
};
